'use client'

import type { ReactNode } from 'react'

import { DIFFICULTIES, type Difficulty } from '@/lib/losi-games/difficulty'
import type { GameTheme } from '@/lib/losi-games/theme'
import type { LosiGameRankWhisper } from '@/lib/losi-games/types'

interface GameShellProps {
  theme: GameTheme
  title: string
  score: number
  best: number
  meta?: string
  menu?: boolean
  over?: boolean
  won?: boolean
  whisper?: LosiGameRankWhisper | null
  hint?: string
  onStart: (diff: Difficulty) => void
  onRestart: () => void
  children: ReactNode
}

export function GameShell({
  theme,
  title,
  score,
  best,
  meta,
  menu = false,
  over = false,
  won = false,
  whisper = null,
  hint,
  onStart,
  onRestart,
  children
}: GameShellProps) {
  const tone = theme.paperDark ? 'text-white/70' : 'text-black/70'
  const strong = theme.paperDark ? 'text-white' : 'text-black'
  const mute = theme.paperDark ? 'text-white/40' : 'text-black/40'
  const frameShadow = theme.paperDark
    ? '0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.08)'
    : '0 14px 36px rgba(10,10,12,0.14), 0 0 0 1px rgba(10,10,12,0.08)'
  const showCard = menu || over || won

  return (
    <div className="mx-auto flex h-full w-full max-w-full flex-col gap-3 p-4">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className={tone}>{title}</span>
        {!menu ? (
          <span className={`tabular-nums ${strong}`}>
            {score}
            <span className={mute}> · best {best}</span>
            {meta ? <span className={mute}> · {meta}</span> : null}
          </span>
        ) : null}
      </div>

      <div className="relative w-full min-h-0 flex-1">
        <div
          className="flex h-full min-h-[min(78vh,860px)] w-full items-stretch justify-stretch overflow-hidden rounded-2xl"
          style={{ background: theme.paper, boxShadow: frameShadow }}
        >
          {children}
        </div>

        {showCard ? (
          <div
            className="absolute inset-0 flex items-center justify-center rounded-2xl p-4"
            style={{
              background: theme.paperDark ? 'rgba(0,0,0,0.55)' : 'rgba(10,10,12,0.42)'
            }}
          >
            <div
              className="w-full max-w-xs rounded-2xl border px-5 py-4 shadow-xl"
              style={{
                background: theme.paper,
                borderColor: theme.mute,
                color: theme.paperDark ? '#f1efe9' : '#0a0a0c',
                boxShadow: frameShadow
              }}
            >
              {menu ? (
                <>
                  <div className="text-base font-semibold">{title}</div>
                  <p className={`mt-1 text-[11px] leading-snug ${mute}`}>
                    Choose a difficulty, then play.
                  </p>
                  <div className="mt-4 flex flex-col gap-2">
                    {DIFFICULTIES.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        className="w-full cursor-pointer rounded-xl px-3 py-2.5 text-sm font-medium transition hover:opacity-90"
                        style={{ background: theme.body, color: theme.paper }}
                        onClick={() => onStart(d.id)}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                  {best > 0 ? (
                    <p className={`mt-3 text-[11px] tabular-nums ${mute}`}>Best {best}</p>
                  ) : null}
                </>
              ) : (
                <>
                  <div className="text-base font-semibold">{won ? 'Cleared' : 'Game over'}</div>
                  <div className="mt-3 space-y-1 text-sm tabular-nums">
                    <div className="flex justify-between gap-4">
                      <span className={mute}>Score</span>
                      <span>{score}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className={mute}>Best</span>
                      <span>{best}</span>
                    </div>
                    {whisper?.newBest ? (
                      <div className="pt-1 text-xs font-medium" style={{ color: theme.mark }}>
                        New high score
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="mt-4 w-full cursor-pointer rounded-xl px-3 py-2 text-sm font-medium"
                    style={{ background: theme.body, color: theme.paper }}
                    onClick={onRestart}
                  >
                    Play again
                  </button>
                  {whisper ? (
                    <p className={`mt-3 text-[11px] leading-snug ${mute}`}>{whisper.label}</p>
                  ) : null}
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {hint && !showCard ? <p className={`text-xs ${mute}`}>{hint}</p> : null}
    </div>
  )
}
